import { inject, Injectable, makeStateKey, PLATFORM_ID, signal, TransferState } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { defer, map, Observable, of, shareReplay, tap } from 'rxjs';
import { ApiService } from '../../../shared/services/api.service';
import { withTransferState } from '../../../shared/utils/transfer-state.util';
import { PublicHomeBlock, PublicHomePayload } from './home-blocks.model';

// Clave de TransferState para pasar los bloques del home del render server al
// cliente, evitando el re-fetch tras la hidratación (el "flash" data→skeleton).
const HOME_BLOCKS_KEY = makeStateKey<PublicHomeBlock[]>('perron.home.blocks');

/**
 * Cliente del endpoint `GET /api/web/home`. Devuelve un array `blocks[]`
 * ordenado por `sort_order` ascendente; el front renderiza estrictamente en
 * el orden recibido. Bloques inactivos o vacíos ya fueron filtrados en
 * backend — el front no necesita validar contenido per-type.
 *
 * Cache de sesión vía `shareReplay(1)`: primer suscriptor dispara la
 * request, el resto recibe el mismo valor sin re-pedirlo.
 *
 * Además usa `TransferState` (patrón de `CategoryService`): el server serializa
 * los bloques y el cliente hidrata con ellos sin volver a pedir `/web/home`.
 * Como la request lleva `Authorization`, queda fuera del transfer-cache HTTP
 * automático de Angular, así que la propagamos a mano.
 */
@Injectable({ providedIn: 'root' })
export class HomeBlocksService {
  private readonly api = inject(ApiService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly transferState = inject(TransferState);

  // Snapshot sincrónico (para clientes que prefieran chequear cache antes de
  // suscribirse). Hoy no se usa externamente; queda como hook futuro.
  private readonly _cachedBlocks = signal<PublicHomeBlock[] | null>(null);
  private cachedRequest$?: Observable<PublicHomeBlock[]>;

  /**
   * Carga los bloques del home. Devuelve `[]` si el payload viene vacío (caso
   * legítimo: todos los bloques inactivos), o si el campo `blocks` no es array
   * (defensa contra respuestas malformadas durante prerender).
   */
  getHome(): Observable<PublicHomeBlock[]> {
    const cached = this._cachedBlocks();
    if (cached) return of(cached);

    if (!this.cachedRequest$) {
      // `defer` para que la request sólo se dispare en un MISS del TransferState.
      this.cachedRequest$ = withTransferState(
        this.transferState,
        this.isBrowser,
        HOME_BLOCKS_KEY,
        defer(() =>
          this.api
            .get<PublicHomePayload>('/web/home')
            .pipe(map((res) => (Array.isArray(res?.blocks) ? res.blocks : []))),
        ),
      ).pipe(
        tap((blocks) => this._cachedBlocks.set(blocks)),
        shareReplay(1),
      );
    }
    return this.cachedRequest$;
  }
}
